/**
 * « Paie livreurs » (migration 0044) — primes de forte demande par livreur et par mois.
 *
 * Tout ce qui DÉCIDE est dans la base : le rapport (`paie_livreurs_mois`) calcule les primes sur les courses
 * livrées, ou rend l'INSTANTANÉ d'un mois déjà payé (un mois payé ne se recalcule plus) ; « marquer comme payé »
 * (`marquer_primes_payees`) calcule le montant lui-même, refuse un mois en cours ou une période close, et est
 * idempotent. Ce module met en forme, exporte et traduit les refus — logique PURE testée, plus deux appels.
 *
 * Le salaire de base n'est PAS ici : il reste une dépense « salaire » saisie à la main (compte 6211).
 */
import { supabase } from './supabase'

export type MoyenPaie = 'especes' | 'wave' | 'orange_money' | 'virement'

export const MOYENS_PAIE: readonly { valeur: MoyenPaie; libelle: string }[] = [
  { valeur: 'especes', libelle: 'Espèces' },
  { valeur: 'wave', libelle: 'Wave' },
  { valeur: 'orange_money', libelle: 'Orange Money' },
  { valeur: 'virement', libelle: 'Virement' },
]

export const libelleMoyenPaie = (m: string): string => MOYENS_PAIE.find((x) => x.valeur === m)?.libelle ?? m

export interface CoursePrime {
  course_id: string
  ref: string
  livree_le: string
  /** Majoration payée par le client. */
  majoration_fcfa: number
  /** Part reversée au livreur. */
  prime_fcfa: number
}

export interface PaiementPrime {
  id: string
  montant_fcfa: number
  moyen: MoyenPaie
  reference: string | null
  paye_le: string
  paye_par_nom: string | null
}

export interface LignePaie {
  driver_id: string
  nom: string
  courses_livrees: number
  courses_majorees: number
  prime_fcfa: number
  majoration_fcfa: number
  detail: CoursePrime[]
  /** `null` = pas encore payé (les chiffres sont calculés) ; sinon l'instantané du paiement fait foi. */
  paiement: PaiementPrime | null
}

export interface RapportPaie {
  /** Premier jour du mois, `AAAA-MM-01`. */
  mois: string
  libelle_mois: string
  /** Le mois est terminé (heure de Bamako) : on peut le payer. */
  termine: boolean
  livreurs: LignePaie[]
  total_prime_fcfa: number
  total_paye_fcfa: number
  total_a_payer_fcfa: number
}

type Brut = Record<string, unknown>
const nb = (v: unknown): number => (v == null || v === '' ? 0 : Number(v))
const txt = (v: unknown): string => (v == null ? '' : String(v))
const liste = (v: unknown): Brut[] => (Array.isArray(v) ? (v as Brut[]) : [])

/** Lecture tolérante du jsonb rendu par `paie_livreurs_mois`. */
export function lireRapportPaie(brut: unknown): RapportPaie {
  const o = (brut ?? {}) as Brut
  return {
    mois: txt(o.mois).slice(0, 10),
    libelle_mois: txt(o.libelle_mois),
    termine: o.termine === true,
    livreurs: liste(o.livreurs).map((l) => {
      const p = l.paiement && typeof l.paiement === 'object' ? (l.paiement as Brut) : null
      return {
        driver_id: txt(l.driver_id),
        nom: txt(l.nom) || '—',
        courses_livrees: nb(l.courses_livrees),
        courses_majorees: nb(l.courses_majorees),
        prime_fcfa: nb(l.prime_fcfa),
        majoration_fcfa: nb(l.majoration_fcfa),
        detail: liste(l.detail).map((d) => ({
          course_id: txt(d.course_id),
          ref: txt(d.ref),
          livree_le: txt(d.livree_le),
          majoration_fcfa: nb(d.majoration_fcfa),
          prime_fcfa: nb(d.prime_fcfa),
        })),
        paiement: p
          ? {
              id: txt(p.id),
              montant_fcfa: nb(p.montant_fcfa),
              moyen: txt(p.moyen) as MoyenPaie,
              reference: p.reference == null ? null : txt(p.reference),
              paye_le: txt(p.paye_le),
              paye_par_nom: p.paye_par_nom == null ? null : txt(p.paye_par_nom),
            }
          : null,
      }
    }),
    total_prime_fcfa: nb(o.total_prime_fcfa),
    total_paye_fcfa: nb(o.total_paye_fcfa),
    total_a_payer_fcfa: nb(o.total_a_payer_fcfa),
  }
}

/** Le mois précédent (`AAAA-MM-01`) : celui qu'on paie le plus souvent. Calcul en UTC = heure de Bamako. */
export function moisPrecedent(maintenant: Date = new Date()): string {
  const d = new Date(Date.UTC(maintenant.getUTCFullYear(), maintenant.getUTCMonth() - 1, 1))
  return d.toISOString().slice(0, 10)
}

/** Les douze derniers mois, le plus récent d'abord (mois en cours compris), pour le sélecteur. */
export function derniersMois(maintenant: Date = new Date(), n = 12): string[] {
  return Array.from({ length: n }, (_, i) => new Date(Date.UTC(maintenant.getUTCFullYear(), maintenant.getUTCMonth() - i, 1)).toISOString().slice(0, 10))
}

const MOIS_FR = ['janvier', 'février', 'mars', 'avril', 'mai', 'juin', 'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre']
export const libelleMois = (m: string): string => `${MOIS_FR[Number(m.slice(5, 7)) - 1] ?? ''} ${m.slice(0, 4)}`

/**
 * Les lignes de l'export CSV : une ligne par course majorée (le détail que réclame la paie), précédée du
 * livreur, puis un total par livreur et le total du mois. Les montants sont des nombres (Excel les additionne).
 */
export function lignesCsvPaie(r: RapportPaie): (string | number)[][] {
  const L: (string | number)[][] = [
    ['Mois', 'Livreur', 'Courses livrées', 'Courses majorées', 'Référence course', 'Livrée le', 'Majoration client (F)', 'Part livreur (F)', 'Statut', 'Payé le', 'Moyen', 'Référence paiement'],
  ]
  for (const l of r.livreurs) {
    const statut = l.paiement ? 'Payé' : l.prime_fcfa > 0 ? 'À payer' : 'Aucune prime'
    const paye = l.paiement ? [l.paiement.paye_le.slice(0, 10), libelleMoyenPaie(l.paiement.moyen), l.paiement.reference ?? ''] : ['', '', '']
    for (const c of l.detail) {
      L.push([r.mois.slice(0, 7), l.nom, '', '', c.ref, c.livree_le.slice(0, 10), c.majoration_fcfa, c.prime_fcfa, statut, ...paye])
    }
    L.push([r.mois.slice(0, 7), l.nom, l.courses_livrees, l.courses_majorees, 'TOTAL LIVREUR', '', l.majoration_fcfa, l.prime_fcfa, statut, ...paye])
  }
  L.push([r.mois.slice(0, 7), 'TOTAL DU MOIS', '', '', '', '', r.livreurs.reduce((s, l) => s + l.majoration_fcfa, 0), r.total_prime_fcfa, `dont payé ${r.total_paye_fcfa}`, '', '', ''])
  return L
}

const ERREURS: Record<string, string> = {
  forbidden: 'Seuls le propriétaire et le comptable gèrent la paie des livreurs.',
  mois_non_termine: 'Ce mois n’est pas terminé : ses dernières livraisons ne sont pas toutes connues. Payez-le à partir du 1er du mois suivant.',
  livreur_inconnu: 'Livreur inconnu dans cette organisation.',
  moyen_invalide: 'Choisissez un moyen de paiement : espèces, Wave, Orange Money ou virement.',
  reference_invalide: 'La référence du paiement est limitée à 100 caractères.',
  rien_a_payer: 'Ce livreur n’a aucune prime de forte demande ce mois-ci.',
  montant_change: 'Le montant dû a changé depuis l’affichage : rechargez l’écran avant de payer.',
  periode_close: 'La période comptable est clôturée : rouvrez l’exercice pour enregistrer ce paiement.',
  mois_requis: 'Choisissez un mois.',
}

/** Traduit le message d'erreur d'un RPC de paie ; un message inconnu est rendu tel quel. */
export function traduireErreurPaie(message: string): string {
  for (const [code, phrase] of Object.entries(ERREURS)) if (message.includes(code)) return phrase
  if (/paie_livreurs_mois|marquer_primes_payees/.test(message) && /does not exist|introuvable|Could not find/i.test(message)) {
    return 'La paie des livreurs n’est pas encore disponible : la migration 0044 n’est pas appliquée à la base.'
  }
  return message
}

export async function chargerRapportPaie(orgId: string, mois: string): Promise<RapportPaie> {
  const { data, error } = await supabase.rpc('paie_livreurs_mois', { p_org: orgId, p_mois: mois })
  if (error) throw new Error(traduireErreurPaie(error.message))
  return lireRapportPaie(data)
}

/** Rend `deja_paye` sans erreur si quelqu'un a payé entre-temps : rien n'est payé deux fois. */
export async function marquerPrimesPayees(
  orgId: string,
  p: { driverId: string; mois: string; moyen: MoyenPaie; reference: string; montantAffiche: number },
): Promise<'ok' | 'deja_paye'> {
  const { data, error } = await supabase.rpc('marquer_primes_payees', {
    p_org: orgId,
    p_driver: p.driverId,
    p_mois: p.mois,
    p_moyen: p.moyen,
    p_reference: p.reference.trim() || null,
    p_montant_attendu: p.montantAffiche,
  })
  if (error) throw new Error(traduireErreurPaie(error.message))
  return (data as { code?: string } | null)?.code === 'deja_paye' ? 'deja_paye' : 'ok'
}
